import { ReactNode, useEffect, useState } from 'react';
import { InlineLoading, ToastNotification } from '@carbon/react';

import { ROTULOS, rotuloDoAviso } from './rotulos';
import styles from './avisos.module.scss';

// OS AVISOS DE RESULTADO DE AÇÃO — o "Salvo", o "Não consegui apagar" — no `ToastNotification` do
// Carbon, numa pilha no canto de cima (`AvisosDoCarbon`, montado uma vez no App).
//
// A regra do Carbon para toasts: o de sucesso some sozinho em 5 segundos; o de erro e o de aviso
// ficam até a pessoa fechar (um erro que some antes de ser lido não aconteceu para ela). Erro de um
// formulário não é toast: fica junto do formulário (`InlineNotification` ou o campo inválido).
//
// A chamada é uma função (`message.success('Salvo')`) porque o resultado chega fora de componente,
// num `catch` ou num serviço.
//
// ⚠️ Os avisos são funções soltas, chamadas fora de componente (em `catch`, em serviço). Por isso
// a pilha mora num registro do módulo, e não num contexto do React.

type Tipo = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface Aviso {
  id: string;
  tipo: Tipo;
  conteudo: ReactNode;
  /** Em segundos. 0 é "até alguém fechar". */
  duracao: number;
}

// O tempo do Carbon para o toast que some sozinho.
const DURACAO_PADRAO = 5;
// Erro e aviso ficam até alguém fechar.
const FICA: Tipo[] = ['error', 'warning', 'loading'];
let avisos: Aviso[] = [];
let sequencia = 0;
const ouvintes = new Set<(a: Aviso[]) => void>();

const avisar = () => ouvintes.forEach((f) => f(avisos));

const fechar = (id?: string) => {
  avisos = id ? avisos.filter((a) => a.id !== id) : [];
  avisar();
};

/** Uma função que fecha o aviso, e que também se pode esperar (`await`) até ele sair. */
type Fechar = (() => void) & PromiseLike<boolean>;

const abrir = (tipo: Tipo, conteudo: ReactNode, duracao = DURACAO_PADRAO, chave?: string): Fechar => {
  const id = chave ?? `aviso-${(sequencia += 1)}`;
  const novo: Aviso = { id, tipo, conteudo, duracao };
  // A mesma chave substitui o aviso anterior (o "Salvando…" que vira "Salvo").
  avisos = avisos.some((a) => a.id === id) ? avisos.map((a) => (a.id === id ? novo : a)) : [...avisos, novo];
  avisar();
  const terminou = new Promise<boolean>((resolver) => {
    if (duracao > 0) setTimeout(() => { fechar(id); resolver(true); }, duracao * 1000);
  });
  const f = (() => fechar(id)) as Fechar;
  f.then = terminou.then.bind(terminou) as Fechar['then'];
  return f;
};

type Conteudo = ReactNode | { content: ReactNode; duration?: number; key?: string };

const deTipo = (tipo: Tipo) => (conteudo: Conteudo, duracao?: number) => {
  if (conteudo && typeof conteudo === 'object' && 'content' in (conteudo as object)) {
    const c = conteudo as { content: ReactNode; duration?: number; key?: string };
    return abrir(tipo, c.content, c.duration ?? duracao ?? (FICA.includes(tipo) ? 0 : DURACAO_PADRAO), c.key);
  }
  return abrir(tipo, conteudo as ReactNode, duracao ?? (FICA.includes(tipo) ? 0 : DURACAO_PADRAO));
};

export const message = {
  success: deTipo('success'),
  error: deTipo('error'),
  warning: deTipo('warning'),
  info: deTipo('info'),
  loading: deTipo('loading'),
  open: ({ type = 'info', content, duration, key }: { type?: Tipo; content: ReactNode; duration?: number; key?: string }) =>
    abrir(type, content, duration ?? DURACAO_PADRAO, key),
  destroy: (chave?: string) => fechar(chave),
};

const TIPO_DO_CARBON = { success: 'success', error: 'error', warning: 'warning', info: 'info', loading: 'info' } as const;

/** A pilha de avisos. Montada uma vez, no App. */
export function AvisosDoCarbon() {
  const [lista, setLista] = useState<Aviso[]>(avisos);
  useEffect(() => {
    ouvintes.add(setLista);
    return () => { ouvintes.delete(setLista); };
  }, []);

  if (!lista.length) return null;
  return (
    <div className={styles.pilha} aria-live='polite'>
      {lista.map((a) => (
        <ToastNotification
          key={a.id}
          kind={TIPO_DO_CARBON[a.tipo]}
          role={a.tipo === 'error' ? 'alert' : 'status'}
          // Texto vai no título (é o que o Carbon destaca); qualquer outra coisa, no corpo.
          title={a.tipo !== 'loading' && typeof a.conteudo === 'string' ? a.conteudo : ''}
          hideCloseButton={a.tipo === 'loading'}
          timeout={0}
          onClose={() => { fechar(a.id); return false; }}
          aria-label='Fechar aviso'
          statusIconDescription={rotuloDoAviso(TIPO_DO_CARBON[a.tipo])}
        >
          {a.tipo === 'loading'
            ? <InlineLoading description={a.conteudo} iconDescription={ROTULOS.carregando} />
            : typeof a.conteudo === 'string' ? null : a.conteudo}
        </ToastNotification>
      ))}
    </div>
  );
}
